import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';

// Web Vitals monitoring utility
export const initWebVitalsMonitoring = () => {
  // Only run in production
  if (process.env.NODE_ENV !== 'production') {
    return;
  }

  const sendToAnalytics = (metric) => {
    // Send to your analytics service
    console.log('Web Vital:', metric);
    
    // Example: Send to Google Analytics 4
    if (window.gtag) {
      window.gtag('event', metric.name, {
        event_category: 'Web Vitals',
        event_label: metric.id,
        value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
        custom_map: {
          metric_id: 'dimension1',
          metric_value: 'metric1',
          metric_delta: 'metric2',
        },
      });
    }

    // Example: Send to PostHog if available
    if (window.posthog) {
      window.posthog.capture('web_vital_measured', {
        metric_name: metric.name,
        metric_value: metric.value,
        metric_id: metric.id,
        metric_delta: metric.delta,
        metric_rating: metric.rating,
      });
    }
  };

  // Monitor all Core Web Vitals (updated for web-vitals v5)
  onCLS(sendToAnalytics);
  onFCP(sendToAnalytics);
  onINP(sendToAnalytics); // INP replaces FID in v5
  onLCP(sendToAnalytics);
  onTTFB(sendToAnalytics);
};

// Performance observer for custom metrics
export const observePerformance = () => {
  if (!('PerformanceObserver' in window)) {
    return;
  }

  // Observe long tasks (for TBT calculation)
  const longTaskObserver = new PerformanceObserver((list) => {
    const entries = list.getEntries();
    entries.forEach((entry) => {
      if (entry.duration > 50) {
        console.log('Long task detected:', entry.duration, 'ms');
        
        // Send to analytics
        if (window.posthog) {
          window.posthog.capture('long_task_detected', {
            duration: entry.duration,
            start_time: entry.startTime,
            name: entry.name,
          });
        }
      }
    });
  });

  longTaskObserver.observe({ entryTypes: ['longtask'] });

  // Observe layout shifts
  const layoutShiftObserver = new PerformanceObserver((list) => {
    const entries = list.getEntries();
    entries.forEach((entry) => {
      if (entry.hadRecentInput) {
        return; // Ignore layout shifts from user input
      }
      
      console.log('Layout shift detected:', entry.value);
      
      // Send to analytics
      if (window.posthog) {
        window.posthog.capture('layout_shift_detected', {
          value: entry.value,
          start_time: entry.startTime,
          sources: entry.sources?.map(source => ({
            node: source.node?.tagName,
            previousRect: source.previousRect,
            currentRect: source.currentRect,
          })),
        });
      }
    });
  });

  layoutShiftObserver.observe({ entryTypes: ['layout-shift'] });

  // Observe resource timing
  const resourceObserver = new PerformanceObserver((list) => {
    const entries = list.getEntries();
    const slowResources = entries.filter(entry => 
      entry.duration > 1000 || // Resources taking more than 1 second
      (entry.transferSize > 100000) // Resources larger than 100KB
    );

    slowResources.forEach((entry) => {
      console.log('Slow resource:', entry.name, entry.duration, 'ms');
      
      // Send to analytics
      if (window.posthog) {
        window.posthog.capture('slow_resource_detected', {
          name: entry.name,
          duration: entry.duration,
          transfer_size: entry.transferSize,
          encoded_body_size: entry.encodedBodySize,
          decoded_body_size: entry.decodedBodySize,
        });
      }
    });
  });

  resourceObserver.observe({ entryTypes: ['resource'] });
};

// Utility to measure custom metrics
export const measureCustomMetric = (name, startTime = performance.now()) => {
  return {
    end: () => {
      const duration = performance.now() - startTime;
      console.log(`Custom metric ${name}:`, duration, 'ms');
      
      // Send to analytics
      if (window.posthog) {
        window.posthog.capture('custom_metric_measured', {
          metric_name: name,
          duration: duration,
        });
      }
      
      return duration;
    },
  };
};

// Initialize all performance monitoring
export const initPerformanceMonitoring = () => {
  initWebVitalsMonitoring();
  observePerformance();
  
  // Monitor page load metrics
  window.addEventListener('load', () => {
    setTimeout(() => {
      const navigation = performance.getEntriesByType('navigation')[0];
      
      console.log('Page Load Metrics:', {
        domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
        loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
        totalPageLoad: navigation.loadEventEnd - navigation.fetchStart,
      });
      
      // Send to analytics
      if (window.posthog) {
        window.posthog.capture('page_load_metrics', {
          dom_content_loaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
          load_complete: navigation.loadEventEnd - navigation.loadEventStart,
          total_page_load: navigation.loadEventEnd - navigation.fetchStart,
        });
      }
    }, 0);
  });
};
